/**
 * The shop's technicians whose time is MECHANICAL repair capacity — the
 * database's work-role answer (`shop_mechanical_technician_ids`, built on
 * membership_work_roles). A detail-only specialist is not in it; a
 * TECHNICIAN + DETAIL_SPECIALIST person is; a person nobody has stated roles
 * for counts (legacy).
 *
 * Two readings of the same query:
 *
 *  - `useMechanicalRoster` says WHICH of three things is true: still loading,
 *    unavailable (the read failed or was refused), or known. The load index
 *    divides by capacity, so it must never treat "could not read" as "nobody
 *    is mechanical" — that is how OBLI showed 100 beside a full crew.
 *  - `useMechanicalTechnicianIds` keeps its original contract for advisory
 *    consumers: the set when known, `null` otherwise — callers then exclude
 *    nobody (the pre-multi-role answer), never everybody.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type MechanicalRoster =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | { status: 'known'; ids: ReadonlySet<string> };

type RosterAnswer = { ok: true; ids: ReadonlySet<string> } | { ok: false };

function useMechanicalRosterQuery(shopId: string | null | undefined) {
  return useQuery({
    queryKey: ['shop-mechanical-technician-ids', shopId],
    enabled: !!shopId,
    retry: false,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<RosterAnswer> => {
      const { data: ids, error } = await (supabase as any).rpc('shop_mechanical_technician_ids', { p_shop_id: shopId });
      if (error) {
        console.error('[mechanical-capacity] shop_mechanical_technician_ids failed:', error);
        return { ok: false };
      }
      return {
        ok: true,
        ids: new Set(((ids ?? []) as unknown[]).map((r) =>
          typeof r === 'string' ? r : String((r as { id?: string })?.id ?? r))),
      };
    },
  });
}

export function useMechanicalRoster(shopId: string | null | undefined): MechanicalRoster {
  const { data, isError } = useMechanicalRosterQuery(shopId);
  if (isError || data?.ok === false) return { status: 'unavailable' };
  if (!data) return { status: 'loading' };
  return { status: 'known', ids: data.ids };
}

export function useMechanicalTechnicianIds(shopId: string | null | undefined): ReadonlySet<string> | null {
  const { data } = useMechanicalRosterQuery(shopId);
  return data?.ok ? data.ids : null;
}
